<template>
    <div v-if="localVisibel">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div class="max-h-[700px] overflow-auto">
                <hts-step-by-step-travel
                    :steps="CStages"
                    direction="horizontal"
                    class="my-20"
                    @clickStep="clickStep">
                    <template #feltInStep="step: any">
                        <div
                            :class="{
                                'text-main-text': !step.step.warning,
                                'text-error-500': step.step.warning === 'red',
                                'opacity-50': step.step.disable,
                                'text-sm': step.step.stepType === 'task'
                            }">
                            <p class="text-right font-bold">
                                {{ step.step.time }}
                            </p>
                            <p class="text-right">
                                {{ step.step.day }}
                            </p>
                        </div>
                    </template>
                    <template #rightInStep="step: any">
                        <div
                            v-if="step.step.stepType === 'node'"
                            :class="{
                                'text-main-text': !step.step.warning,
                                'text-error-500': step.step.warning === 'red',
                                'opacity-50': step.step.disable,
                                'text-sm': step.step.stepType === 'task'
                            }">
                            <div class="flex gap-2 w-full">
                                <p class="font-bold max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap">
                                    {{ step.step.nodeLocationName }}
                                </p>
                                <div class="relative">
                                    <span class="absolute top-0 left-0 -translate-y-3 flex gap-1">
                                        <div
                                            v-if="step.step.vehicle"
                                            class="w-7 h-7">
                                            <img
                                                class="w-full"
                                                :src="vehicle" >
                                        </div>

                                        <hts-icon-svg
                                            v-if="step.step.waitingTime"
                                            name="hourglass"
                                            class-name="h-7 w-7 fill-yellow-500" />
                                    </span>
                                </div>
                            </div>

                            <p>
                                {{ step.step.locationName }}
                            </p>
                        </div>
                        <div
                            v-if="step.step.stepType === 'task'"
                            class="text-sm"
                            :class="{
                                'text-main-text': step.step.warning === '',
                                'text-error-500': step.step.warning === 'red',
                                'opacity-50': step.step.disable
                            }">
                            <div class="flex gap-2 w-full">
                                <p class="font-bold max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap">
                                    {{ step.step.name }}
                                </p>
                                <div class="relative">
                                    <span class="absolute top-0 left-0 -translate-y-3 flex gap-1">
                                        <hts-icon-svg
                                            v-if="step.step.waitingTime"
                                            name="hourglass"
                                            class-name="h-7 w-7 fill-yellow-500" />
                                    </span>
                                </div>
                            </div>

                            <p
                                v-for="time in step.step.timeConditions"
                                :key="time"
                                :class="{
                                    'text-success-500': time.timeType === 'EARLIEST',
                                    'text-warning-500': time.timeType === 'LATEST'
                                }">
                                {{ time.label }}
                            </p>
                        </div>
                    </template>
                </hts-step-by-step-travel>
            </div>

            <div class="">
                <hts-map-route
                    ref="map"
                    center-marker
                    class="h-full"
                    :route="markers"
                    :route-separated="routes"
                    :draggable="false"
                    :show-location="showLocation"
                    :lat-lng-location-routes="CLatLngLocationRoutes">
                    <!--
                      <template #top>
                      <div class="flex min-h-5 justify-end gap-3">
                      <hts-map-icon-location />
                      </div>
                      </template>
                    -->
                </hts-map-route>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import routeMap from '@/pages/trucker-[id]/route/ignore/route-map'

const { markers, setMarkers, routes, setRoutes, mapLoading, getRoutingPosition, setRoutingPositions, showLocation, draggable, CLatLngLocationRoutes } =
  routeMap()

const { $formatters } = useNuxtApp()
const vehicle = ref('/assets/vehicle.png')

const emits = defineEmits(['modify', 'update-route'])
const localTravelDetails = ref<{ [key: string]: any }[]>([])
const localPositionsAndWaitingTasks = ref<{ [key: string]: any }[]>([])
const localVisibel = ref<boolean>(false)

const props = defineProps({
  visibel: {
    type: Boolean
  },
  journey: {
    type: Object
  },
  travelDetails: {
    type: Array<{ [key: string]: any }>
  },
  positionsAndWaitingTasks: {
    type: Array<{ [key: string]: any }>
  },
  findRoute: {
    type: Function
  },
  allocationStatus: {
    type: Number,
    default: 4
  }
})

const { visibel, journey, travelDetails, positionsAndWaitingTasks, findRoute, allocationStatus } = toRefs(props)

const btnFindRoute = ref({
  loading: false
})

const showModalNotFoundRoute = ref(false)
const showModalConfirmChangeRoute = ref(false)
const showModalWarningStatus = ref(false)
const route = ref({} as any)

const map = ref<any>(null)
onMounted(() => {
  localVisibel.value = visibel?.value || false
  localTravelDetails.value = travelDetails?.value || []
  localPositionsAndWaitingTasks.value = positionsAndWaitingTasks?.value || []

  if (journey?.value?.routingPositions) {
    map.value?.ready().then(() => {
      setRoutingPositions(journey?.value?.routingPositions)
      setMarkers(journey?.value?.positions)
      setRoutes(journey?.value?.positions)
    })
  }
})

watch(
  () => props.visibel,
  (newVal) => {
    localVisibel.value = newVal
  }
)

watch(
  () => props.travelDetails,
  (newVal) => {
    localTravelDetails.value = newVal || []
  }
)

watch(
  () => props.positionsAndWaitingTasks,
  (newVal) => {
    localPositionsAndWaitingTasks.value = newVal || []
  }
)

const show = (journey: any) => {
  localVisibel.value = true
  localTravelDetails.value = journey.travelDetails
  localPositionsAndWaitingTasks.value = journey.positionsAndWaitingTasks

  if (journey.routingPositions) {
    map.value?.ready().then(() => {
      setRoutingPositions(journey.routingPositions)
      setMarkers(journey.positions)
      setRoutes(journey.positions)
    })
  }
}

const close = () => {
  localVisibel.value = false
  localTravelDetails.value = []
  localPositionsAndWaitingTasks.value = []
}

const nodeShowTasks = ref([] as string[])

const getTravelDetail = (type: 'stage' | 'task', stageIndex: number, taskIndex?: number) => {
  const stage = localTravelDetails.value?.find((item: any) => item.index === stageIndex && item.type === 'stage')

  if (!stage) {
    return
  }

  if (type === 'stage') {
    return stage
  }

  if (type === 'task') {
    if (typeof taskIndex !== 'number') {
      return
    }

    return localTravelDetails.value?.find((item: any) => item.stageIndex === stage.stageIndex && item.index === taskIndex && item.type === type)
  }
}

const getWarning = (type: 'stage' | 'task', stageIndex: number, taskIndex?: number) => {
  const stage = localTravelDetails.value?.find((item: any) => item.index === stageIndex && item.type === 'stage')

  if (!stage) {
    return
  }
  let processAlertStatus = 0
  if (type === 'stage') {
    const tmp = localTravelDetails.value?.filter((item: any) => item.stageIndex === stage.stageIndex && item.type === 'task')
    if (tmp) {
      tmp.forEach((item: any) => {
        if (item.processAlertStatus > processAlertStatus) {
          processAlertStatus = item.processAlertStatus
        }
      })
    }
  }

  if (type === 'task') {
    if (typeof taskIndex === 'number') {
      const tmp = localTravelDetails.value?.find((item: any) => item.stageIndex === stage.stageIndex && item.index === taskIndex && item.type === 'task')

      if (tmp) {
        if (tmp.processAlertStatus > processAlertStatus) {
          processAlertStatus = tmp.processAlertStatus
        }
      }
    }
  }

  if (processAlertStatus >= 30) {
    return 'red'
  }
  return ''
}

const getTravelDetailStage = (stageIndex: number) => {
  return getTravelDetail('stage', stageIndex)
}

const getTravelDetailTask = (stageIndex: number, taskIndex: number) => {
  return getTravelDetail('task', stageIndex, taskIndex)
}

const getWarningStage = (stageIndex: number) => {
  return getWarning('stage', stageIndex)
}

const getWarningTask = (stageIndex: number, taskIndex: number) => {
  return getWarning('task', stageIndex, taskIndex)
}

const getTimeConditions = (timeConditions: { [key: string]: any }[], type: 'START' | 'END') => {
  const values = [] as { [key: string]: any }[]

  if (!timeConditions) {
    return values
  }

  timeConditions?.forEach((item: { [key: string]: any }) => {
    let textType = ''
    let textTimeType = ''
    const time = $formatters.datetime(item.time, 'DD/MM/YYYY HH:mm')
    if (item.type === 'START') {
      textType = 'Bắt đầu'
    }

    if (item.type === 'END') {
      textType = 'Kết thúc'
    }

    if (item.timeType === 'EARLIEST') {
      textTimeType = 'sớm nhất'
    }

    if (item.timeType === 'LATEST') {
      textTimeType = 'muộn nhất'
    }

    if (type === item.type && textType && textTimeType && time) {
      values.push({
        label: `${textType} ${textTimeType} ${time}`,
        timeType: item.timeType
      })
    }
  })

  return values
}

const CStages = computed(() => {
  const value = [] as { [key: string]: any }[]
  let valueStageIndex = 0

  if (!localPositionsAndWaitingTasks.value) {
    return []
  }

  let vehiclePassed = false
  const disable = false
  // stage
  localPositionsAndWaitingTasks.value?.forEach((j: any, stageIndex: number) => {
    const data = $lodash.cloneDeep(j)
    data.stepType = 'node'
    data.nodeLocationName += ` - Điểm ${stageIndex + 1}`
    data.warning = getWarningStage(stageIndex)

    const travelData = getTravelDetailStage(stageIndex)

    if (travelData) {
      const eta = travelData.eta
      data.time = $formatters.datetime(eta, 'HH:mm')
      data.day = $formatters.datetime(eta, 'DD/MM/YYYY')
      data.estimate = true
      data.disable = disable
      data.waitingTime = travelData.waitingTime || 0
    }
    data.vehiclePassed = vehiclePassed
    value.push(data)
    valueStageIndex = value.length - 1

    if (nodeShowTasks.value.includes(j._id)) {
      const tasks = j.tasks
      value[valueStageIndex].showTask = true
      value[valueStageIndex].vehiclePassed = vehiclePassed
      tasks?.forEach((t: any, taskIndex: number) => {
        const tIn = $lodash.cloneDeep(t)
        const tOut = $lodash.cloneDeep(t)
        tIn.stepType = 'task'
        tOut.stepType = 'task'

        const travelDataTask = getTravelDetailTask(stageIndex, taskIndex)

        // task bắt đầu - kết thúc
        if (travelDataTask) {
          // bắt đầu
          const eta = travelDataTask.eta
          tIn.time = $formatters.datetime(eta, 'HH:mm')
          tIn.day = $formatters.datetime(eta, 'DD/MM/YYYY')
          tIn.warning = getWarningTask(stageIndex, taskIndex)
          tIn.timeConditions = getTimeConditions(t.timeConditions, 'START')

          if (!travelDataTask.ata) {
            tIn.estimate = true
          }

          if (travelDataTask.ata) {
            tIn.disable = false
          } else {
            vehiclePassed = false
          }

          tIn.vehiclePassed = vehiclePassed
          tIn.name = `Bắt đầu ${t.taskName}`
          if (tIn.type === 'waiting') {
            tIn.name = `Bắt đầu chờ`
          }
          tIn.waitingTime = travelDataTask.waitingTime || 0

          value.push(tIn)
          // kết thúc

          const etd = travelDataTask.etd
          tOut.time = $formatters.datetime(etd, 'HH:mm')
          tOut.day = $formatters.datetime(etd, 'DD/MM/YYYY')
          tOut.warning = getWarningTask(stageIndex, taskIndex)
          tOut.timeConditions = getTimeConditions(t.timeConditions, 'END')
          tOut.disable = disable
          tOut.vehiclePassed = vehiclePassed
          tOut.name = `Kết thúc ${t.taskName}`
          if (tOut.type === 'waiting') {
            tOut.name = `Kết thúc chờ`
          }
          value.push(tOut)
        }
      })
    }
  })

  return value
})

const clickStep = (value: any) => {
  if (!value?._id) {
    return
  }

  if (nodeShowTasks.value.includes(value._id)) {
    nodeShowTasks.value = nodeShowTasks.value.filter((id) => id !== value._id)
    return
  }
  nodeShowTasks.value.push(value._id)
}

const handleFindRoute = async () => {
  btnFindRoute.value.loading = true
  if (!findRoute?.value) {
    btnFindRoute.value.loading = false
    return
  }

  if (allocationStatus.value === 5 || allocationStatus.value === 6) {
    btnFindRoute.value.loading = false
    showModalWarningStatus.value = true
    return
  }

  route.value = await findRoute.value({ modules: ['ROUTE'] })

  if (!route.value) {
    showModalNotFoundRoute.value = true
  } else {
    showModalConfirmChangeRoute.value = true
  }
  btnFindRoute.value.loading = false
}

const handleConfirmChangeRoute = () => {
  showModalConfirmChangeRoute.value = false
  emits('update-route', route.value)
}
defineExpose({
  show,
  close
})
</script>
